import React, { useState, useEffect } from 'react';
import { X, ChevronDown } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { useToast } from '../hooks/use-toast';
import { validateEmail, validateName, validatePhoneNumber, sanitizePhoneInput } from '../utils/validation';
import { countryCodes } from '../mock';
import { getGeolocation, submitLead, getDialCodeFromCountryCode } from '../services/apiService';

const RegistrationModal = ({ isOpen, onClose }) => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    countryCode: '+1',
    country: 'US',
    phone: '',
    description: ''
  });
  const [errors, setErrors] = useState({
    name: '',
    email: '',
    phone: '',
    description: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [geoLocationData, setGeoLocationData] = useState(null);

  // Fetch geolocation when modal opens
  useEffect(() => {
    if (isOpen && !geoLocationData) {
      const fetchGeolocation = async () => {
        const geoData = await getGeolocation();
        setGeoLocationData(geoData);
        
        if (geoData.success && geoData.countryCode) {
          const dialCode = getDialCodeFromCountryCode(geoData.countryCode);
          setFormData(prev => ({
            ...prev,
            countryCode: dialCode,
            country: geoData.countryCode
          }));
        }
      };

      fetchGeolocation();
    }
  }, [isOpen]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    let sanitizedValue = value;
    
    // Sanitize phone input - only allow digits
    if (name === 'phone') {
      sanitizedValue = sanitizePhoneInput(value);
      // Limit to 10 digits
      if (sanitizedValue.length > 10) {
        sanitizedValue = sanitizedValue.slice(0, 10);
      }
    }
    
    setFormData(prev => ({
      ...prev,
      [name]: sanitizedValue
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Reset errors
    let newErrors = {
      name: '',
      email: '',
      phone: '',
      description: ''
    };
    
    let hasErrors = false;
    
    // Validate name
    if (!validateName(formData.name)) {
      newErrors.name = 'Name must be at least 2 characters long';
      hasErrors = true;
    }
    
    // Validate email - use proper object return
    const emailValidation = validateEmail(formData.email);
    if (!emailValidation.isValid) {
      newErrors.email = emailValidation.error;
      hasErrors = true;
    }
    
    // Validate phone - must be exactly 10 digits
    if (!formData.phone || !validatePhoneNumber(formData.phone)) {
      newErrors.phone = 'Please enter a valid 10-digit phone number (e.g., 1234567890)';
      hasErrors = true;
    }
    
    // Validate description
    if (formData.description.trim().length < 10) {
      newErrors.description = 'Description must be at least 10 characters long';
      hasErrors = true;
    }
    
    if (hasErrors) {
      setErrors(newErrors);
      return;
    }

    setIsSubmitting(true);
    
    // Prepare data for API
    const leadData = {
      name: formData.name,
      email: formData.email,
      countryDialCode: formData.countryCode,
      country: formData.country,
      phoneNumber: formData.phone, // Only numeric
      description: formData.description
    };

    // Submit to CRM API
    const result = await submitLead(leadData, geoLocationData);

    setIsSubmitting(false);

    if (result.success) {
      console.log('Modal form submission successful:', result);
      toast({
        title: "Success!",
        description: "Registration submitted successfully. Redirecting..."
      });
      
      // Reset form
      setFormData({
        name: '',
        email: '',
        countryCode: '+1',
        country: 'US',
        phone: '',
        description: ''
      });
      setErrors({
        name: '',
        email: '',
        phone: '',
        description: ''
      });
      
      // Close modal and redirect
      onClose();
      console.log('Redirecting to:', result.redirectUrl);
      setTimeout(() => {
        window.location.href = result.redirectUrl;
      }, 500);
    } else {
      console.error('Modal submission failed:', result.error);
      toast({
        title: "Error",
        description: result.error || "Failed to submit. Please try again.",
        variant: "destructive"
      });
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm animate-in fade-in duration-200">
      <div className="relative bg-slate-900 rounded-2xl shadow-2xl max-w-4xl w-full overflow-hidden border border-slate-700 animate-in zoom-in-95 duration-300">
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 z-10 p-2 rounded-full bg-slate-800/50 hover:bg-slate-700 transition-colors duration-200 group"
        >
          <X className="w-5 h-5 text-gray-400 group-hover:text-white transition-colors" />
        </button>

        <div className="grid md:grid-cols-2 h-full">
          {/* Left Side - Image */}
          <div className="hidden md:block relative bg-gradient-to-br from-blue-900 via-blue-800 to-blue-700 overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-r from-amber-500/10 to-transparent"></div>
            <div className="relative h-full flex flex-col justify-center items-center p-8 text-white">
              <img
                src="https://customer-assets.emergentagent.com/job_brand-registry-2/artifacts/6fg5cpq6_img-1.jpg"
                alt="Registration"
                className="w-full h-full object-cover opacity-30 absolute inset-0"
              />
              <div className="relative z-10 text-center">
                <h2 className="text-3xl font-bold mb-3">Start Your Journey</h2>
                <p className="text-base text-blue-100 mb-5">
                  Protect your brand with our expert trademark registration services
                </p>
                <div className="space-y-2 text-left text-sm">
                  <div className="flex items-center">
                    <div className="w-2 h-2 bg-amber-400 rounded-full mr-2"></div>
                    <span>Fast 24-hour processing</span>
                  </div>
                  <div className="flex items-center">
                    <div className="w-2 h-2 bg-amber-400 rounded-full mr-2"></div>
                    <span>Expert legal guidance</span>
                  </div>
                  <div className="flex items-center">
                    <div className="w-2 h-2 bg-amber-400 rounded-full mr-2"></div>
                    <span>Affordable pricing from $45</span>
                  </div>
                  <div className="flex items-center">
                    <div className="w-2 h-2 bg-amber-400 rounded-full mr-2"></div>
                    <span>100% satisfaction guarantee</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right Side - Form */}
          <div className="bg-slate-900 p-6 md:p-8">
            <div className="mb-4">
              <h3 className="text-2xl font-bold text-white mb-1">Get Started Today</h3>
              <p className="text-sm text-gray-400">Fill in your details and we'll get back to you within 24 hours</p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="modal-name" className="text-sm text-gray-300">Full Name *</Label>
                <Input
                  id="modal-name"
                  name="name"
                  type="text"
                  placeholder="Enter your full name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  className={`mt-1 bg-slate-800/50 border-slate-600 text-white placeholder:text-gray-500 focus:border-blue-500 transition-colors h-10 ${errors.name ? 'border-red-500 focus:border-red-500' : ''}`}
                />
                {errors.name && (
                  <p className="text-red-400 text-xs mt-1 flex items-center">
                    <span className="mr-1">⚠</span> {errors.name}
                  </p>
                )}
              </div>

              <div>
                <Label htmlFor="modal-email" className="text-sm text-gray-300">Email Address *</Label>
                <Input
                  id="modal-email"
                  name="email"
                  type="email"
                  placeholder="user@example.com"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  className={`mt-1 bg-slate-800/50 border-slate-600 text-white placeholder:text-gray-500 focus:border-blue-500 transition-colors h-10 ${errors.email ? 'border-red-500 focus:border-red-500' : ''}`}
                />
                {errors.email && (
                  <p className="text-red-400 text-xs mt-1 flex items-center">
                    <span className="mr-1">⚠</span> {errors.email}
                  </p>
                )}
              </div>

              <div>
                <Label htmlFor="modal-phone" className="text-sm text-gray-300">Contact Number *</Label>
                <div className="mt-1 flex gap-2">
                  {/* Country Code Dropdown */}
                  <div className="relative">
                    <select
                      name="countryCode"
                      value={formData.countryCode}
                      onChange={handleInputChange}
                      className="h-10 px-3 py-2 bg-slate-800/50 border border-slate-600 text-white text-sm rounded-md appearance-none cursor-pointer hover:bg-slate-800 focus:border-blue-500 focus:outline-none transition-colors pr-8"
                    >
                      {countryCodes.map((country, index) => (
                        <option key={index} value={country.code}>
                          {country.flag} {country.code}
                        </option>
                      ))}
                    </select>
                    <ChevronDown className="absolute right-2 top-1/2 transform -translate-y-1/2 w-3 h-3 text-gray-400 pointer-events-none" />
                  </div>
                  
                  {/* Phone Number Input */}
                  <Input
                    id="modal-phone"
                    name="phone"
                    type="tel"
                    placeholder="1234567890 (10 digits)"
                    value={formData.phone}
                    onChange={handleInputChange}
                    maxLength="10"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    required
                    className={`flex-1 bg-slate-800/50 border-slate-600 text-white placeholder:text-gray-500 focus:border-blue-500 transition-colors h-10 ${errors.phone ? 'border-red-500 focus:border-red-500' : ''}`}
                  />
                </div>
                {errors.phone && (
                  <p className="text-red-400 text-xs mt-1 flex items-center">
                    <span className="mr-1">⚠</span> {errors.phone}
                  </p>
                )}
              </div>

              <div>
                <Label htmlFor="modal-description" className="text-sm text-gray-300">Description *</Label>
                <Textarea
                  id="modal-description"
                  name="description"
                  placeholder="Tell us about your trademark registration needs..."
                  value={formData.description}
                  onChange={handleInputChange}
                  required
                  className={`mt-1 min-h-[80px] bg-slate-800/50 border-slate-600 text-white placeholder:text-gray-500 focus:border-blue-500 transition-colors resize-none ${errors.description ? 'border-red-500 focus:border-red-500' : ''}`}
                />
                {errors.description && (
                  <p className="text-red-400 text-xs mt-1 flex items-center">
                    <span className="mr-1">⚠</span> {errors.description}
                  </p>
                )}
              </div>

              <Button
                type="submit"
                className="w-full bg-gradient-to-r from-blue-600 to-blue-700 hover:from-amber-500 hover:to-amber-600 text-white py-5 transition-all duration-300 shadow-lg hover:shadow-xl"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <span className="flex items-center justify-center">
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Submitting...
                  </span>
                ) : (
                  'Submit Registration'
                )}
              </Button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RegistrationModal;
